import { Modal, Space, Typography, Button, Spin, List, Tag, Alert, theme } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined, UserOutlined, MailOutlined, BankOutlined, TeamOutlined, FileTextOutlined } from '@ant-design/icons';
import { UserWithTenant } from '@/features/superadmin/types';

const { Text, Paragraph } = Typography;

interface UserDependencies {
  hasClientes: boolean;
  hasContratos: boolean;
  clientesCount: number;
  contratosCount: number;
  totalDependencies: number;
}

interface DeleteUserDialogAdvancedProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithTenant | null;
  dependencies: UserDependencies | null;
  isLoading: boolean;
  onConfirm: (id: string, forceUnlink: boolean) => Promise<void>;
}

export function DeleteUserDialogAdvanced({
  open,
  onOpenChange,
  user,
  dependencies,
  isLoading,
  onConfirm,
}: DeleteUserDialogAdvancedProps) {
  const { token } = theme.useToken();

  if (!user) {
    return null;
  }

  const userName = user.first_name
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : 'Este usuário';

  const userEmail = user.email || 'Sem email';
  const hasDependencies = dependencies && dependencies.totalDependencies > 0;

  const handleConfirm = () => {
    onConfirm(user.id, true);
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Confirmar Exclusão de Usuário</span>
        </Space>
      }
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={[
        <Button key="cancel" onClick={() => onOpenChange(false)} disabled={isLoading}>
          Cancelar
        </Button>,
        <Button key="delete" type="primary" danger onClick={handleConfirm} loading={isLoading}>
          Confirmar Exclusão
        </Button>
      ]}
      width={600}
      styles={{
        header: { padding: '20px 24px 0' },
        body: { padding: '20px 24px' },
        footer: { padding: '16px 24px', margin: 0 },
      }}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ backgroundColor: token.colorErrorBg, padding: 16, borderRadius: 8, border: `1px solid ${token.colorErrorBorder}` }}>
          <Paragraph style={{ margin: 0 }}>
            Você está prestes a excluir permanentemente o usuário:
          </Paragraph>
          <div style={{ marginTop: 8 }}>
            <Space orientation="vertical" size={2}>
              <Text strong><UserOutlined /> {userName}</Text>
              <Text type="secondary"><MailOutlined /> {userEmail}</Text>
              {user.tenant && <Text type="secondary"><BankOutlined /> {user.tenant.nome}</Text>}
            </Space>
          </div>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Spin tip="Verificando dependências...">
              <div style={{ padding: 20 }} />
            </Spin>
          </div>
        ) : hasDependencies ? (
          <div style={{ backgroundColor: token.colorWarningBg, padding: 16, borderRadius: 8, border: `1px solid ${token.colorWarningBorder}` }}>
            <Space align="start" style={{ marginBottom: 12 }}>
              <InfoCircleOutlined style={{ color: '#faad14', marginTop: 4 }} />
              <Text strong>Atenção: Este usuário possui registros vinculados!</Text>
            </Space>

            <List size="small" split={false}>
              {dependencies?.clientesCount > 0 && (
                <List.Item>
                  <Space><TeamOutlined /> <Text>{dependencies.clientesCount} clientes cadastrados</Text></Space>
                </List.Item>
              )}
              {dependencies?.contratosCount > 0 && (
                <List.Item>
                  <Space><FileTextOutlined /> <Text>{dependencies.contratosCount} contratos vinculados</Text></Space>
                </List.Item>
              )}
            </List>

            <Paragraph style={{ marginTop: 12, marginBottom: 0, fontSize: '13px' }} type="secondary">
              Ao confirmar, estes registros serão desvinculados do usuário, mas permanecerão no sistema.
            </Paragraph>
          </div>
        ) : (
          <Alert
            message="Este usuário não possui registros vinculados e pode ser excluído com segurança."
            type="info"
            showIcon
          />
        )}

        <Text type="danger" style={{ fontSize: '12px' }}>
          * Esta ação é irreversível e o acesso do usuário será revogado imediatamente.
        </Text>
      </Space>
    </Modal>
  );
}
